<template>
  <MTPage>
    <!-- Loading state -->
    <div v-if="loading" class="flex justify-center py-12">
      <MTIcon name="loader" :size="32" class="animate-spin text-gray-400" />
    </div>

    <!-- Lesson not found -->
    <div v-else-if="!lesson" class="text-center py-12">
      <MTIcon name="alert-circle" :size="48" class="text-gray-400 mx-auto mb-4" />
      <h2 class="text-xl font-semibold mb-2">Lesson not found</h2>
      <p class="text-[var(--mt-text-muted)] mb-4">
        We couldn't find this lesson in the curriculum.
      </p>
      <MTButton variant="primary" @click="goBack">Go Back</MTButton>
    </div>

    <!-- Lesson locked / plan upgrade required -->
    <div v-else-if="!isLessonAccessible" class="lhp-locked-wall">
      <div class="lhp-lock-icon">
        <MTIcon name="lock" :size="36" />
      </div>
      <h2 class="lhp-lock-title">This lesson is locked</h2>
      <p class="lhp-lock-body">
        <strong>{{ lesson.title }}</strong> requires
        {{ lessonUpgradeLabel ? `a ${lessonUpgradeLabel} plan` : 'an upgraded plan' }}
        to access. Upgrade to unlock the full curriculum.
      </p>
      <div class="flex gap-3 justify-center flex-wrap">
        <MTButton variant="secondary" @click="goBack">Go Back</MTButton>
        <RouterLink to="/parent/subscription">
          <MTButton variant="primary">Upgrade Plan</MTButton>
        </RouterLink>
      </div>
    </div>

    <!-- Main content -->
    <template v-else>
      <div>

        <!-- ============================= -->
        <!-- 1. HERO SECTION               -->
        <!-- ============================= -->
        <section class="mb-5">
          <div class="hub-hero-card bg-[var(--mt-card-bg)] rounded-2xl shadow-sm border border-[var(--mt-border)] p-4">
            <!-- Row 1: Icon + Title + badge -->
            <div class="flex items-center justify-between gap-3 mb-2">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-[var(--mt-accent-blue-muted)] flex items-center justify-center flex-shrink-0">
                  <MTIcon name="book-open" :size="22" class="text-[var(--mt-accent-blue)]" />
                </div>
                <h1 class="text-xl sm:text-2xl font-extrabold text-[var(--mt-text)]">{{ lesson.title }}</h1>
              </div>
              <MasteryBadge :band="lessonBand" size="sm" class="shrink-0" />
            </div>

            <!-- Row 2: Stats + Practice All button -->
            <div class="flex items-center justify-between gap-3">
              <p class="text-xs text-[var(--mt-text-muted)] font-medium">
                {{ subsections.length }} {{ subsections.length === 1 ? 'skill' : 'skills' }}
                <span class="opacity-40 mx-1">&middot;</span>
                {{ videoCount }} videos
                <span class="opacity-40 mx-1">&middot;</span>
                {{ questionCount }} questions
              </p>
              <button
                class="hub-cta-btn inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all whitespace-nowrap"
                @click="practiceAll"
              >
                <MTIcon name="play-circle" :size="14" />
                Practice All
                <MTIcon name="arrow-right" :size="12" />
              </button>
            </div>
          </div>
        </section>

        <!-- ============================= -->
        <!-- 2. TOPIC TABS                 -->
        <!-- ============================= -->
        <div class="hub-tab-scroll flex gap-2 overflow-x-auto pb-1 mb-5">
          <button
            v-for="(sub, idx) in subsections"
            :key="sub.content"
            class="hub-pill-tab flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all"
            :class="activeTopicIndex === idx
              ? 'hub-pill-active text-white shadow-md'
              : isSecurePlus(sub.content)
                ? 'bg-green-50 text-green-800 border border-green-200 shadow-sm'
                : 'bg-[var(--mt-card-bg)] text-[var(--mt-text)] border border-[var(--mt-border)] shadow-sm'"
            @click="switchTopic(idx)"
          >
            <!-- Mini progress ring -->
            <div class="relative w-8 h-8 flex-shrink-0">
              <svg class="w-full h-full" viewBox="0 0 32 32">
                <circle cx="16" cy="16" r="12" fill="none"
                  :stroke="activeTopicIndex === idx ? 'rgba(255,255,255,0.25)' : (isSecurePlus(sub.content) ? '#bbf7d0' : '#e5e7eb')"
                  stroke-width="2.5"/>
                <circle
                  v-if="getTopicAccuracy(sub.content) > 0"
                  cx="16" cy="16" r="12" fill="none"
                  :stroke="activeTopicIndex === idx ? 'white' : bandStrokeColor(getBand(sub.content))"
                  stroke-width="2.5"
                  :stroke-dasharray="tabCircumference"
                  :stroke-dashoffset="tabCircumference - (tabCircumference * getTopicAccuracy(sub.content) / 100)"
                  class="hub-progress-ring"
                  stroke-linecap="round"
                />
              </svg>
              <span
                class="absolute inset-0 flex items-center justify-center text-[9px] font-extrabold"
                :class="activeTopicIndex === idx
                  ? 'text-white'
                  : isSecurePlus(sub.content)
                    ? 'text-green-700'
                    : (getTopicAccuracy(sub.content) > 0 ? 'text-[var(--mt-text)]' : 'text-[var(--mt-text-muted)]')"
              >{{ getTopicAccuracy(sub.content) }}</span>
            </div>
            {{ sub.title || sub.content }}
            <!-- "Done" badge for secure+ topics -->
            <span
              v-if="isSecurePlus(sub.content)"
              class="ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none"
              :class="activeTopicIndex === idx ? 'bg-white/20 text-white' : 'bg-green-100 text-green-700'"
            >Done</span>
          </button>
        </div>

        <!-- ============================= -->
        <!-- 3. LEARNING CONTENT SECTION   -->
        <!-- ============================= -->
        <div class="hub-learn-section rounded-2xl border shadow-sm mb-5 overflow-hidden">
          <!-- Learn Header (collapsible toggle) -->
          <button
            class="w-full flex items-center justify-between px-5 py-4 hover:opacity-80 transition-colors"
            @click="learnExpanded = !learnExpanded"
          >
            <div class="flex items-center gap-2.5">
              <span class="text-xl">&#128214;</span>
              <h2 class="text-lg font-extrabold text-[var(--mt-text)]">
                Learn: {{ activeTopic?.title || activeTopic?.content }}
              </h2>
            </div>
            <MTIcon
              :name="learnExpanded ? 'chevron-up' : 'chevron-down'"
              :size="20"
              class="text-[var(--mt-text-muted)] transition-transform duration-300"
            />
          </button>

          <!-- Learn Body (expandable) -->
          <div
            class="hub-learn-body overflow-hidden transition-all duration-400"
            :class="learnExpanded ? 'hub-learn-expanded' : 'hub-learn-collapsed'"
          >
            <!-- No content state -->
            <div v-if="!activeTopicMaterial && !materialsLoading" class="px-5 py-8 text-center">
              <MTIcon name="book-open" :size="40" class="text-gray-300 mx-auto mb-3" />
              <p class="text-[var(--mt-text-muted)] mb-3">Learning content coming soon for this topic.</p>
              <button
                class="text-sm font-bold text-[var(--mt-accent-blue)] hover:underline"
                @click="scrollToPractice"
              >
                Skip to practice &darr;
              </button>
            </div>

            <template v-else-if="activeTopicMaterial">
              <!-- Content Tabs: Images | Summary | Key Points | Examples -->
              <div class="flex gap-0 px-5 border-b border-[var(--mt-border)] opacity-80">
                <button
                  v-if="activeTopicMaterial.overviewImages?.length"
                  class="hub-learn-tab px-4 py-3 text-sm font-semibold border-b-2 transition-all"
                  :class="activeLearnTab === 'images'
                    ? 'hub-learn-tab-active text-[var(--mt-primary)] border-[var(--mt-primary)]'
                    : 'text-[var(--mt-text-muted)] border-transparent hover:text-[var(--mt-text)]'"
                  @click="activeLearnTab = 'images'"
                >
                  <span class="flex items-center gap-1.5">
                    <MTIcon name="image" :size="16" />
                    Images
                  </span>
                </button>
                <button
                  v-if="activeTopicMaterial.text"
                  class="hub-learn-tab px-4 py-3 text-sm font-semibold border-b-2 transition-all"
                  :class="activeLearnTab === 'summary'
                    ? 'hub-learn-tab-active text-[var(--mt-primary)] border-[var(--mt-primary)]'
                    : 'text-[var(--mt-text-muted)] border-transparent hover:text-[var(--mt-text)]'"
                  @click="activeLearnTab = 'summary'"
                >
                  <span class="flex items-center gap-1.5">
                    <MTIcon name="file-text" :size="16" />
                    Summary
                  </span>
                </button>
                <button
                  v-if="activeTopicMaterial.keyPoints?.length"
                  class="hub-learn-tab px-4 py-3 text-sm font-semibold border-b-2 transition-all"
                  :class="activeLearnTab === 'keypoints'
                    ? 'hub-learn-tab-active text-[var(--mt-primary)] border-[var(--mt-primary)]'
                    : 'text-[var(--mt-text-muted)] border-transparent hover:text-[var(--mt-text)]'"
                  @click="activeLearnTab = 'keypoints'"
                >
                  <span class="flex items-center gap-1.5">
                    <MTIcon name="list-checks" :size="16" />
                    Key Points
                  </span>
                </button>
                <button
                  v-if="activeTopicMaterial.examples?.length"
                  class="hub-learn-tab px-4 py-3 text-sm font-semibold border-b-2 transition-all"
                  :class="activeLearnTab === 'examples'
                    ? 'hub-learn-tab-active text-[var(--mt-primary)] border-[var(--mt-primary)]'
                    : 'text-[var(--mt-text-muted)] border-transparent hover:text-[var(--mt-text)]'"
                  @click="activeLearnTab = 'examples'"
                >
                  <span class="flex items-center gap-1.5">
                    <MTIcon name="lightbulb" :size="16" />
                    Examples
                  </span>
                </button>
              </div>

              <!-- Tab Content Panels -->
              <div class="px-5 py-5">
                <!-- SUMMARY -->
                <div v-if="activeLearnTab === 'summary' && activeTopicMaterial.text">
                  <div class="hub-prose text-[var(--mt-text)] leading-relaxed text-base" v-html="activeTopicMaterial.text" />
                </div>

                <!-- KEY POINTS -->
                <div v-if="activeLearnTab === 'keypoints' && activeTopicMaterial.keyPoints?.length">
                  <h3 class="text-sm font-bold text-[var(--mt-text-muted)] uppercase tracking-wide mb-3">Key Points to Remember</h3>
                  <div class="space-y-3">
                    <div
                      v-for="(point, idx) in activeTopicMaterial.keyPoints"
                      :key="idx"
                      class="flex items-start gap-3 bg-[var(--mt-card-bg)] rounded-xl border border-[var(--mt-border)] px-4 py-3"
                    >
                      <div class="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MTIcon name="check" :size="14" class="text-green-600" />
                      </div>
                      <span class="text-base text-[var(--mt-text)] font-medium">{{ point }}</span>
                    </div>
                  </div>
                </div>

                <!-- EXAMPLES -->
                <div v-if="activeLearnTab === 'examples' && activeTopicMaterial.examples?.length">
                  <WorkedExampleBlock
                    v-for="(example, idx) in activeTopicMaterial.examples"
                    :key="idx"
                    :example="example"
                    :index="idx"
                  />
                </div>

                <!-- IMAGES CAROUSEL -->
                <div v-if="activeLearnTab === 'images' && activeTopicMaterial.overviewImages?.length">
                  <div class="relative max-w-4xl mx-auto">
                    <!-- Image -->
                    <div class="rounded-xl overflow-hidden bg-gray-50 border border-[var(--mt-border)]">
                      <img
                        :src="activeTopicMaterial.overviewImages[carouselIndex].url"
                        :alt="activeTopicMaterial.overviewImages[carouselIndex].alt || 'Concept diagram'"
                        class="w-full h-auto block"
                        loading="lazy"
                      />
                    </div>

                    <!-- Prev / Next arrows (only if more than 1 image) -->
                    <template v-if="activeTopicMaterial.overviewImages.length > 1">
                      <button
                        class="hub-carousel-btn absolute left-2 top-1/2 -translate-y-1/2"
                        :disabled="carouselIndex === 0"
                        @click="carouselPrev"
                      >
                        <MTIcon name="chevron-left" :size="20" />
                      </button>
                      <button
                        class="hub-carousel-btn absolute right-2 top-1/2 -translate-y-1/2"
                        :disabled="carouselIndex === activeTopicMaterial.overviewImages.length - 1"
                        @click="carouselNext"
                      >
                        <MTIcon name="chevron-right" :size="20" />
                      </button>
                    </template>
                  </div>

                  <!-- Dot indicators -->
                  <div v-if="activeTopicMaterial.overviewImages.length > 1" class="flex justify-center gap-2 mt-3">
                    <button
                      v-for="(_, idx) in activeTopicMaterial.overviewImages"
                      :key="idx"
                      class="w-2 h-2 rounded-full transition-all"
                      :class="carouselIndex === idx ? 'bg-[var(--mt-primary)] w-4' : 'bg-gray-300 hover:bg-gray-400'"
                      @click="carouselIndex = idx"
                    />
                  </div>
                </div>
              </div>

              <!-- "Got it! Let's practice" CTA -->
              <div class="px-5 pb-5">
                <button
                  class="hub-cta-btn w-full py-4 rounded-xl text-white font-extrabold text-base shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                  @click="scrollToPractice"
                >
                  <MTIcon name="check" :size="20" />
                  Got it! Let's practice
                  <MTIcon name="arrow-down" :size="20" />
                </button>
              </div>
            </template>

            <!-- Loading materials -->
            <div v-else-if="materialsLoading" class="px-5 py-8 text-center">
              <MTIcon name="loader" :size="24" class="animate-spin text-gray-400 mx-auto mb-2" />
              <p class="text-sm text-[var(--mt-text-muted)]">Loading learning content...</p>
            </div>
          </div>
        </div>

        <!-- ============================= -->
        <!-- 4. PRACTICE SECTION           -->
        <!-- ============================= -->
        <section id="practice-section" class="bg-[var(--mt-card-bg)] rounded-2xl border border-[var(--mt-border)] shadow-sm mb-5 overflow-hidden">

          <!-- Practice Complete -->
          <LessonCompleteCard
            v-if="engine.lessonReady.value"
            :title="completionTitle"
            :type="engine.completionType.value || 'lesson'"
            :band="engine.lessonBand.value"
            @next="handleNext"
            @repeat="repeatPractice"
          />

          <template v-else>
            <!-- Practice Header -->
            <div class="px-5 py-4 border-b border-[var(--mt-border)]">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="text-xl">&#127919;</span>
                  <h2 class="text-lg font-extrabold text-[var(--mt-text)]">Try It Yourself</h2>
                </div>
                <span
                  v-if="engineStarted && currentSkillLabel"
                  class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                  :style="{ background: 'var(--mt-accent-blue-muted)', color: 'var(--mt-accent-blue)' }"
                >
                  <MTIcon name="target" :size="12" />
                  {{ currentSkillLabel }}
                </span>
              </div>
            </div>

            <!-- Question Area -->
            <div class="p-5 sm:p-6">
              <!-- Not started yet — prompt to begin -->
              <div v-if="!engineStarted" class="text-center py-8">
                <MTIcon name="play-circle" :size="48" class="text-[var(--mt-primary)] mx-auto mb-3" />
                <p class="text-lg font-semibold mb-2">Ready to practice?</p>
                <p class="text-[var(--mt-text-muted)] mb-4">
                  {{ practiceAllMode ? 'Practice questions from all topics in this lesson.' : `Practice questions about ${activeTopic?.title || activeTopic?.content}.` }}
                </p>
                <button
                  class="hub-cta-btn inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white shadow-md hover:shadow-lg transition-all"
                  @click="startPractice"
                >
                  <MTIcon name="play-circle" :size="20" />
                  Start Practicing
                </button>
              </div>

              <!-- Question -->
              <template v-else>
                <!-- Adaptive message -->
                <transition name="fade">
                  <div
                    v-if="engine.adaptiveMessage.value"
                    class="text-sm font-semibold px-4 py-2 rounded-lg bg-yellow-100 text-yellow-900 inline-flex items-center gap-2 mb-4"
                  >
                    {{ engine.adaptiveMessage.value }}
                  </div>
                </transition>

                <div v-if="engine.currentQuestion.value">
                  <QuestionBlock
                    :question="engine.currentQuestion.value.question"
                    :studentId="studentId"
                    @questionCompleted="onQuestionCompleted"
                  />

                  <!-- Hint Link -->
                  <div class="text-center mt-5">
                    <button
                      class="inline-flex items-center gap-2 text-sm text-amber-600 hover:text-amber-700 font-bold transition-colors"
                      @click="showHint = true"
                    >
                      <span class="text-lg">&#128161;</span>
                      Need a hint?
                    </button>
                  </div>
                </div>

                <!-- Loading state -->
                <div v-else-if="engine.isLoading.value" class="text-center py-10">
                  <MTIcon name="loader" :size="32" class="animate-spin text-[var(--mt-primary)] mx-auto mb-3" />
                  <p class="text-[var(--mt-text-muted)]">Loading questions...</p>
                </div>

                <!-- No questions found -->
                <div v-else-if="engine.noQuestionsAvailable.value" class="text-center py-10">
                  <MTIcon name="alert-circle" :size="48" class="text-amber-500 mx-auto mb-3" />
                  <p class="text-lg font-semibold mb-2">No questions available</p>
                  <p class="text-[var(--mt-text-muted)] mb-4">
                    There are no practice questions for this {{ practiceAllMode ? 'lesson' : 'topic' }} yet.
                  </p>
                </div>

                <!-- Questions exhausted -->
                <div v-else-if="engine.questionsExhausted.value" class="text-center py-10">
                  <MTIcon name="trophy" :size="48" class="text-[var(--mt-primary)] mx-auto mb-3" />
                  <p class="text-lg font-semibold mb-2">Great effort!</p>
                  <p class="text-[var(--mt-text-muted)] mb-4">
                    You've completed all available questions.
                    Come back later for more practice, or try another topic.
                  </p>
                  <div class="flex flex-wrap justify-center gap-3">
                    <MTButton v-if="nextTopicIndex !== null" variant="primary" @click="switchTopic(nextTopicIndex)">
                      Continue with next topic
                    </MTButton>
                  </div>
                </div>

                <!-- Session paused -->
                <div v-else-if="engine.sessionPaused.value" class="text-center py-10">
                  <MTIcon name="coffee" :size="48" class="text-[var(--mt-primary)] mx-auto mb-3" />
                  <p class="text-lg font-semibold mb-2">Nice work!</p>
                  <p class="text-[var(--mt-text-muted)] mb-4">
                    You've answered {{ questionsAnswered }} questions this session. Time for a break!
                  </p>
                </div>

                <!-- Fallback loading -->
                <div v-else class="text-center py-10">
                  <MTIcon name="loader" :size="32" class="animate-spin text-[var(--mt-primary)] mx-auto mb-3" />
                  <p class="text-[var(--mt-text-muted)]">Preparing questions...</p>
                </div>
              </template>
            </div>
          </template>
        </section>

        <!-- Next Topic Button -->
        <div v-if="nextTopicIndex !== null && !engine.lessonReady.value" class="flex justify-end mb-6">
          <button
            class="hub-cta-btn flex items-center gap-2 px-5 py-3 rounded-xl text-white text-sm font-bold hover:opacity-90 active:scale-[0.98] transition-all shadow-md whitespace-nowrap"
            @click="switchTopic(nextTopicIndex)"
          >
            Next: {{ subsections[nextTopicIndex]?.title || subsections[nextTopicIndex]?.content }}
            <MTIcon name="arrow-right" :size="16" />
          </button>
        </div>

      </div>

      <!-- ============================= -->
      <!-- 5. HINT PANEL (Overlay)       -->
      <!-- ============================= -->
      <Teleport to="body">
        <!-- Backdrop -->
        <transition name="fade">
          <div
            v-if="showHint"
            class="fixed inset-0 bg-black/30 z-50"
            @click="showHint = false"
          ></div>
        </transition>

        <!-- Hint Panel -->
        <transition name="slide-hint">
          <div
            v-if="showHint"
            class="fixed top-1/2 right-4 sm:right-8 -translate-y-1/2 w-80 sm:w-96 z-50"
          >
            <div class="bg-[var(--mt-card-bg)] rounded-2xl border-2 border-amber-200 shadow-2xl overflow-hidden">
              <!-- Header -->
              <div class="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                <div class="flex items-center gap-2.5">
                  <div class="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                    <span class="text-xl">&#128161;</span>
                  </div>
                  <h4 class="font-extrabold text-gray-800 text-base">Quick Reminder: {{ hintTopicTitle }}</h4>
                </div>
                <button
                  class="w-8 h-8 rounded-lg hover:bg-amber-100 flex items-center justify-center transition-colors"
                  @click="showHint = false"
                >
                  <MTIcon name="x" :size="20" class="text-gray-400" />
                </button>
              </div>

              <!-- Content -->
              <div class="p-5">
                <template v-if="hintKeyPoints.length">
                  <div v-for="(point, idx) in hintKeyPoints" :key="idx" class="mb-3">
                    <p class="text-base text-[var(--mt-text)] leading-relaxed">{{ point }}</p>
                  </div>
                </template>
                <p v-else class="text-base text-[var(--mt-text-muted)] leading-relaxed mb-4">
                  Review the learning section above for tips on this topic.
                </p>

                <button
                  class="w-full py-3.5 rounded-xl bg-amber-50 border-2 border-amber-200 text-amber-700 font-extrabold text-sm hover:bg-amber-100 transition-colors mt-2"
                  @click="showHint = false"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </transition>
      </Teleport>

    </template>
  </MTPage>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";

import MTPage from "@/components/ui/MTPage.vue";
import MTButton from "@/components/ui/MTButton.vue";
import MTIcon from "@/components/ui/MTIcon.vue";
import MasteryBadge from "@/components/ui/MasteryBadge.vue";

import VideoPlayer from "@/components/learning/VideoPlayer.vue";
import WorkedExampleBlock from "@/components/learning/WorkedExampleBlock.vue";
import LessonCompleteCard from "@/components/lesson/LessonCompleteCard.vue";
import QuestionBlock from "@/components/questions/QuestionBlock.vue";

import { useCurriculumStore } from "@/stores/curriculumStore";
import { useProgressStore } from "@/stores/progressStore";
import { useUserStore } from "@/stores/userStore";
import { learningMaterialsApi } from "@/services/learningMaterialsApi";
import { useContinueLearningEngine } from "@/composables/useContinueLearningEngine";
import { isLessonLocked, requiredPlanLabel } from "@/composables/usePlanGating";
import { useAnalytics } from "@/composables/useAnalytics";

const route = useRoute();
const router = useRouter();
const { track } = useAnalytics();
const curriculumStore = useCurriculumStore();
const progressStore = useProgressStore();
const userStore = useUserStore();

const studentId = computed(() => route.params.studentId);
const lessonId = computed(() => route.params.lessonId);

// ---- State ----
const loading = ref(true);
const materialsLoading = ref(false);
const materials = ref({}); // sgId -> material
const activeTopicIndex = ref(0);
const activeLearnTab = ref("summary");
const learnExpanded = ref(true);
const showHint = ref(false);
const engineStarted = ref(false);
const practiceAllMode = ref(false);
const correctCount = ref(0);
const questionsAnswered = ref(0);
const sessionTotal = ref(10);
const carouselIndex = ref(0);

// SVG ring circumference for tab rings (r=9)
const tabCircumference = 2 * Math.PI * 18; // ~75.4

// ---- Engine ----
const engine = useContinueLearningEngine(studentId.value);

// ---- Lesson & Subsections ----
const lesson = computed(() => {
  return curriculumStore.allLessons.find(l => l.id === lessonId.value);
});

const isLessonAccessible = computed(() => {
  if (!lesson.value) return true; // handled by "not found" block
  return !isLessonLocked(lesson.value.excludedPlans, userStore.effectivePlan);
});

const lessonUpgradeLabel = computed(() => {
  if (!lesson.value) return null;
  return requiredPlanLabel(lesson.value.excludedPlans);
});

const subsections = computed(() => {
  if (!lesson.value) return [];
  if (lesson.value.subsections?.length) return lesson.value.subsections;
  if (lesson.value.formatSectionGroups?.length) {
    return lesson.value.formatSectionGroups.map(sg => ({ content: sg, title: sg }));
  }
  return [];
});

const activeTopic = computed(() => subsections.value[activeTopicIndex.value] || null);

const activeTopicMaterial = computed(() => {
  if (!activeTopic.value) return null;
  return materials.value[activeTopic.value.content] || null;
});

// ---- Hint Key Points ----
// In practice-all mode, show hints for the current question's topic, not the selected tab
const hintTopicTitle = computed(() => {
  if (practiceAllMode.value && engine.currentSectionGroupId.value) {
    const sub = subsections.value.find(s => s.content === engine.currentSectionGroupId.value);
    return sub?.title || activeTopic.value?.title || '';
  }
  return activeTopic.value?.title || '';
});

const hintKeyPoints = computed(() => {
  if (practiceAllMode.value && engine.currentSectionGroupId.value) {
    const mat = materials.value[engine.currentSectionGroupId.value];
    return mat?.keyPoints || [];
  }
  return activeTopicMaterial.value?.keyPoints || [];
});

// ---- Stats ----
const videoCount = computed(() => {
  return subsections.value.filter(s => materials.value[s.content]?.video?.url).length;
});

const questionCount = computed(() => {
  // Rough estimate: we don't know exact count without querying, show subsection count * ~15
  return subsections.value.length * 15;
});

// ---- Progress calculations ----
const bandValues = { beginner: 0, developing: 50, secure: 70, confident: 85, mastery: 100 };

const lessonProgress = computed(() => {
  if (!subsections.value.length) return 0;
  const total = subsections.value.reduce((sum, sub) => {
    const band = getBand(sub.content);
    return sum + (bandValues[band] || 0);
  }, 0);
  return Math.round(total / subsections.value.length);
});

const lessonBand = computed(() => {
  const p = lessonProgress.value;
  if (p >= 86) return "mastery";
  if (p >= 75) return "confident";
  if (p >= 65) return "secure";
  if (p >= 40) return "developing";
  return "beginner";
});

// Next topic index (for bottom bar "Next" button)
const nextTopicIndex = computed(() => {
  if (activeTopicIndex.value >= subsections.value.length - 1) return null;
  return activeTopicIndex.value + 1;
});

// First topic in this lesson that hasn't reached secure+ yet
const firstIncompleteTopicIndex = computed(() => {
  return subsections.value.findIndex(s => !isSecurePlus(s.content));
});

// Practice session progress

// Completion title
const completionTitle = computed(() => {
  if (engine.completionType.value === "topic" && activeTopic.value) {
    return activeTopic.value.title || activeTopic.value.content;
  }
  return lesson.value?.title || "Lesson";
});

// Current skill label from engine's active section group
const currentSkillLabel = computed(() => {
  const sgId = engine.currentSectionGroupId.value;
  if (!sgId) return null;
  const sub = subsections.value.find(s => s.content === sgId);
  return sub?.title || sgId;
});

// ---- Helpers ----
function getBand(sectionGroupId) {
  return progressStore.getSectionGroupBand(studentId.value, sectionGroupId) || "beginner";
}

function getTopicAccuracy(sectionGroupId) {
  const p = progressStore.progress[studentId.value]?.[sectionGroupId];
  return p ? Math.round((p.accuracy || 0) * 100) : 0;
}

function bandDotClass(band) {
  return {
    beginner: "bg-blue-400",
    developing: "bg-green-400",
    secure: "bg-yellow-400",
    confident: "bg-orange-400",
    mastery: "bg-purple-500"
  }[band] || "bg-blue-400";
}

function isSecurePlus(sectionGroupId) {
  return ['secure', 'confident', 'mastery'].includes(getBand(sectionGroupId));
}

function bandStrokeColor(band) {
  return {
    beginner: "#60a5fa",
    developing: "#22c55e",
    secure: "#eab308",
    confident: "#f97316",
    mastery: "#a855f7"
  }[band] || "#60a5fa";
}

// ---- Actions ----
function switchTopic(idx) {
  activeTopicIndex.value = idx;
  carouselIndex.value = 0;
  selectDefaultLearnTab();
  // Reset practice back to landing state — don't auto-restart
  if (engineStarted.value) {
    engine.resetSession();
    engineStarted.value = false;
    practiceAllMode.value = false;
    correctCount.value = 0;
    questionsAnswered.value = 0;
  }
  // Scroll to top of page so user sees the learn section for the new topic
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function selectDefaultLearnTab() {
  const mat = activeTopicMaterial.value;
  if (!mat) { activeLearnTab.value = "summary"; return; }
  if (mat.overviewImages?.length) { activeLearnTab.value = "images"; return; }
  if (mat.text) { activeLearnTab.value = "summary"; return; }
  if (mat.keyPoints?.length) { activeLearnTab.value = "keypoints"; return; }
  if (mat.examples?.length) { activeLearnTab.value = "examples"; return; }
  activeLearnTab.value = "summary";
}

function carouselPrev() {
  if (carouselIndex.value > 0) carouselIndex.value--;
}

function carouselNext() {
  const images = activeTopicMaterial.value?.overviewImages;
  if (images && carouselIndex.value < images.length - 1) carouselIndex.value++;
}

function practiceAll() {
  practiceAllMode.value = true;
  scrollToPractice();
  startPractice();
}

async function startPractice() {
  engineStarted.value = true;
  correctCount.value = 0;
  questionsAnswered.value = 0;

  const sgId = practiceAllMode.value ? null : activeTopic.value?.content;
  await engine.startWithLesson(lessonId.value, sgId);

  if (engine.queue.value?.length) {
    sessionTotal.value = engine.queue.value.length;
  }
}

async function restartEngineForTopic(sgId) {
  correctCount.value = 0;
  questionsAnswered.value = 0;
  await engine.startWithLesson(lessonId.value, sgId || null);
  if (engine.queue.value?.length) {
    sessionTotal.value = engine.queue.value.length;
  }
}

async function onQuestionCompleted(result) {
  if (result.correct) correctCount.value++;
  questionsAnswered.value++;
  await engine.submitQuestionResult(result);
}

function handleNext() {
  if (engine.completionType.value === "topic") {
    const idx = firstIncompleteTopicIndex.value;
    if (idx >= 0) {
      // Jump to first topic not yet at secure level
      switchTopic(idx);
    } else {
      // All topics done — go back to curriculum
      router.push(`/student/${studentId.value}/curriculum`);
    }
  } else {
    // Lesson complete: use engine's suggestion
    const suggested = engine.suggestedNextLesson.value;
    if (suggested?.lessonId) {
      router.push(`/student/${studentId.value}/learn/${suggested.lessonId}`);
    } else {
      router.push(`/student/${studentId.value}/curriculum`);
    }
  }
}

async function repeatPractice() {
  engine.resetSession();
  engineStarted.value = false;
  practiceAllMode.value = false;
  correctCount.value = 0;
  questionsAnswered.value = 0;
}

function scrollToPractice() {
  nextTick(() => {
    const el = document.getElementById("practice-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

function goBack() {
  router.push(`/student/${studentId.value}/curriculum`);
}

// ---- Lifecycle ----
onMounted(async () => {
  try {
    if (!curriculumStore.modules.length) {
      await curriculumStore.loadTrack("core");
    }
    progressStore.ensureChild(studentId.value);

    // Handle query params from redirects
    if (route.query.topic) {
      const idx = subsections.value.findIndex(s => s.content === route.query.topic);
      if (idx >= 0) activeTopicIndex.value = idx;
    } else {
      // Auto-select the first topic not yet at secure level
      const firstIncomplete = subsections.value.findIndex(
        s => !['secure', 'confident', 'mastery'].includes(getBand(s.content))
      );
      if (firstIncomplete >= 0) activeTopicIndex.value = firstIncomplete;
    }
    if (route.query.practice === "all") {
      // Auto-start practice all after load
      nextTick(() => practiceAll());
    }

    // Load learning materials
    if (lesson.value) {
      materialsLoading.value = true;
      materials.value = await learningMaterialsApi.getMaterialsForLesson(lesson.value);
      materialsLoading.value = false;
      selectDefaultLearnTab();
    }
  } catch (err) {
    console.error("Failed to load lesson data:", err);
  } finally {
    loading.value = false;
  }
});

// Watch for lesson change (same component reused on route param change)
watch(lessonId, async (newId, oldId) => {
  if (!newId || newId === oldId) return;

  // Reset state for the new lesson — default to first incomplete topic
  const firstIncomplete = subsections.value.findIndex(
    s => !['secure', 'confident', 'mastery'].includes(getBand(s.content))
  );
  activeTopicIndex.value = firstIncomplete >= 0 ? firstIncomplete : 0;
  engineStarted.value = false;
  practiceAllMode.value = false;
  correctCount.value = 0;
  questionsAnswered.value = 0;
  learnExpanded.value = true;
  showHint.value = false;
  materials.value = {};
  engine.resetSession();

  // Load materials for the new lesson
  if (lesson.value) {
    materialsLoading.value = true;
    materials.value = await learningMaterialsApi.getMaterialsForLesson(lesson.value);
    materialsLoading.value = false;
    selectDefaultLearnTab();
  }

  window.scrollTo({ top: 0 });
});

// Watch for active topic material changes to reset learn tab
watch(activeTopicIndex, () => {
  selectDefaultLearnTab();
});

// Watch engine queue length for session total
watch(
  () => engine.queue.value?.length,
  (len) => {
    if (len && sessionTotal.value === 10) sessionTotal.value = len;
  }
);

// Flush progress on leave
onBeforeRouteLeave(() => {
  progressStore.flushLessonProgress(studentId.value);
  // Track abandonment only when a session was active but not yet completed
  if (engine.sessionStarted.value && !engine.lessonReady.value && engine.lesson.value) {
    track('lesson_abandoned', {
      lesson_id: engine.lesson.value.id,
      lesson_title: engine.lesson.value.title,
      module_title: engine.lesson.value.moduleTitle,
      questions_answered: questionsAnswered.value,
    });
  }
});

onBeforeUnmount(() => {
  progressStore.flushLessonProgress(studentId.value);
});
</script>

<style scoped>
/* Locked lesson wall */
.lhp-locked-wall {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 4rem 1.5rem;
  gap: 1.25rem;
  max-width: 480px;
  margin: 0 auto;
}
.lhp-lock-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #fef3c7;
  color: #d97706;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fde68a;
}
.lhp-lock-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--mt-text);
}
.lhp-lock-body {
  font-size: 0.975rem;
  color: var(--mt-text-muted);
  line-height: 1.6;
}

/* Progress ring rotation */
.hub-progress-ring {
  transition: stroke-dashoffset 0.5s ease;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
}

/* Tab scroll hide scrollbar */
.hub-tab-scroll::-webkit-scrollbar { display: none; }
.hub-tab-scroll { -ms-overflow-style: none; scrollbar-width: none; }

/* Learn body collapse animation */
.hub-learn-expanded {
  max-height: 2000px;
  opacity: 1;
}
.hub-learn-collapsed {
  max-height: 0 !important;
  opacity: 0;
}

/* Prose styling for HTML content */
.hub-prose :deep(ul) { list-style-type: disc; padding-left: 1.5rem; }
.hub-prose :deep(ol) { list-style-type: decimal; padding-left: 1.5rem; }
.hub-prose :deep(p) { margin-bottom: 1rem; }
.hub-prose :deep(h3) { font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.5rem; }
.hub-prose :deep(strong) { font-weight: 700; }

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.slide-hint-enter-active, .slide-hint-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-hint-enter-from, .slide-hint-leave-to {
  transform: translateX(100%) translateY(-50%);
  opacity: 0;
}

/* CTA button + progress fill — use primary by default */
.hub-cta-btn {
  background: linear-gradient(to right, var(--mt-primary), var(--mt-primary-dark, var(--mt-primary)));
}
.hub-progress-fill {
  background: linear-gradient(to right, var(--mt-primary), var(--mt-primary-dark, var(--mt-primary)));
}
.hub-practice-fill {
  background: linear-gradient(to right, #4ade80, #10b981);
}

/* Learn section background */
.hub-learn-section {
  background: var(--mt-accent-blue-muted);
  border-color: var(--mt-border);
}

/* Active pill tab */
.hub-pill-active {
  background: var(--mt-primary);
}

/* Carousel nav buttons */
.hub-carousel-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid var(--mt-border);
  box-shadow: 0 1px 4px rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  color: var(--mt-text);
}
.hub-carousel-btn:hover:not(:disabled) {
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}
.hub-carousel-btn:disabled {
  opacity: 0.3;
  cursor: default;
}
</style>

<!-- Theme overrides — non-scoped to avoid Vite stripping -->
<style>
.theme-space .hub-learn-section {
  background: rgba(99, 102, 241, 0.08);
  border-color: rgba(99, 102, 241, 0.2);
}
.theme-space .hub-learn-tab-active {
  color: #93c5fd !important;
  border-color: #93c5fd !important;
}
.theme-trail .hub-learn-section {
  background: rgba(34, 197, 94, 0.08);
  border-color: rgba(34, 197, 94, 0.2);
}
.theme-road .hub-learn-section {
  background: rgba(249, 115, 22, 0.08);
  border-color: rgba(249, 115, 22, 0.2);
}
</style>
